// Thin wrappers over the nine generated auth operations (constitution II; contracts/consumes.md).
// Paths and shapes come only from the generated contract. The app always signs in as `client: 'app'`
// (FR-017), sends emails trimmed (FR-049) and mobiles as 10 digits (FR-005), and never sends a school
// identifier (SA-AUTH-2). Refresh and sign-out send `{ refreshToken }` only when there is one: the
// Android token from secure storage; on the web the HttpOnly cookie travels instead (no body).
import { type ApiClient, unwrap } from "@/api/client";
import type { Api } from "@/api/types";

export type RegisterInput = {
  name: string;
  email: string;
  password: string;
  /** Already normalised to 10 digits (validation.normaliseMobile). */
  mobile: string;
};

type CallOptions = { signal?: AbortSignal };

const CLIENT: Api["Client"] = "app";

export function createAuthApi(client: ApiClient) {
  const refreshBody = (refreshToken: string | null) =>
    refreshToken === null ? {} : { body: { refreshToken } };

  return {
    signInWithEmail(
      email: string,
      password: string,
    ): Promise<Api["AuthSession"]> {
      return unwrap(
        client.POST("/api/v1/auth/email/login", {
          body: { email: email.trim(), password, client: CLIENT },
        }),
      );
    },

    async registerWithEmail(input: RegisterInput): Promise<void> {
      await unwrap(
        client.POST("/api/v1/auth/email/register", {
          body: {
            name: input.name.trim(),
            email: input.email.trim(),
            password: input.password,
            mobile: input.mobile,
          },
        }),
      );
    },

    verifyEmail(token: string): Promise<Api["EmailVerified"]> {
      return unwrap(
        client.POST("/api/v1/auth/email/verify", { body: { token } }),
      );
    },

    async resendVerificationEmail(email: string): Promise<void> {
      await unwrap(
        client.POST("/api/v1/auth/email/verify/resend", {
          body: { email: email.trim() },
        }),
      );
    },

    async requestPasswordReset(email: string): Promise<void> {
      await unwrap(
        client.POST("/api/v1/auth/password/forgot", {
          body: { email: email.trim(), client: CLIENT },
        }),
      );
    },

    resetPassword(
      token: string,
      newPassword: string,
    ): Promise<Api["PasswordResetCompleted"]> {
      return unwrap(
        client.POST("/api/v1/auth/password/reset", {
          body: { token, newPassword },
        }),
      );
    },

    acceptInvite(
      token: string,
      password: string,
    ): Promise<Api["InviteAccepted"]> {
      return unwrap(
        client.POST("/api/v1/auth/invite/accept", {
          body: { token, password },
        }),
      );
    },

    refreshSession(
      refreshToken: string | null,
      options: CallOptions = {},
    ): Promise<Api["AuthSession"]> {
      return unwrap(
        client.POST("/api/v1/auth/session/refresh", {
          ...refreshBody(refreshToken),
          ...options,
        }),
      );
    },

    async signOut(
      refreshToken: string | null,
      options: CallOptions = {},
    ): Promise<void> {
      await unwrap(
        client.POST("/api/v1/auth/session/logout", {
          ...refreshBody(refreshToken),
          ...options,
        }),
      );
    },
  };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
